import React, { useCallback } from 'react';
import { Avatar } from 'views/components/molecules/Avatar';
import { Typography } from 'views/components/atoms/Typography';
import { Color, MAX_WIDTH } from 'views/constants';
import styled from '@emotion/styled';
import { FormattedMessage, FormattedRelativeTime } from 'react-intl';
import { selectUnit } from '@formatjs/intl-utils';
import { Button } from 'views/components/atoms/Button';
import { Icon } from 'views/components/molecules/Icon';
import { Spacer } from 'views/components/atoms/Spacer';
import { localeIds } from 'locals';

const Wrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommentTextWrapper = styled.div`
  word-break: break-word;
  word-break: break-word;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  > :not(:first-of-type) {
    margin-left: 8px;
  }
`;

const ButtonWrapper = styled.div`
  width: 60px;
`;

interface CommentDetailProps {
  id: number;
  iconSrc: string;
  name: string;
  comment: string;
  createdAt: Date;
  allowDelete: boolean;
  reply?: { name: string };
  onReply(id: number): void;
  onDelete(id: number): void;
}

export const CommentDetail: React.FC<CommentDetailProps> = ({
  id,
  iconSrc,
  name,
  comment,
  createdAt,
  allowDelete,
  reply,
  onReply,
  onDelete,
}) => {
  const { value, unit } = selectUnit(createdAt);

  const nl2br = useCallback((text: string) => {
    {
      return text.split('\n').map((str, index) => (
        <React.Fragment key={index}>
          {str}
          <br />
        </React.Fragment>
      ));
    }
  }, []);

  return (
    <Wrapper>
      <HeaderWrapper>
        <Avatar
          src={iconSrc}
          name={name}
          label={
            <Typography bold color={Color.C2}>
              {name}
            </Typography>
          }
        />
        <Typography color={Color.C3}>
          <FormattedRelativeTime value={value} unit={unit} />
        </Typography>
      </HeaderWrapper>
      <Spacer />
      <CommentTextWrapper>
        <Typography>{nl2br(comment)}</Typography>
      </CommentTextWrapper>
      <Spacer />
      <FooterWrapper>
        {reply ? (
          <div>
            <Typography color={Color.C3}>
              <FormattedMessage
                id={localeIds.commentDetail.reply}
                values={{ name: reply.name }}
              />
            </Typography>
          </div>
        ) : null}
        <ButtonWrapper>
          <Button
            style="secondary"
            type="button"
            onClick={(): void => {
              onReply(id);
            }}
          >
            <Icon type="reply" />
          </Button>
        </ButtonWrapper>
        {allowDelete ? (
          <ButtonWrapper>
            <Button
              style="secondary"
              type="button"
              onClick={(): void => {
                onDelete(id);
              }}
            >
              <Icon type="trash" />
            </Button>
          </ButtonWrapper>
        ) : null}
      </FooterWrapper>
    </Wrapper>
  );
};
